import clsx from "clsx";
import { FC } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Move } from "@/assets/icons/ds";
import { Button } from "@/components/Interface/Button/Button";
import { Checkbox } from "@/components/Interface/FormFields/Checkbox/Checkbox";
import { TableSelectAllCheckbox } from "@/components/Layout/Table/TableSelectAllCheckbox/TableSelectAllCheckbox";
import { BillingAccountChip } from "@/components/Portal/Documents/BillingAccounts/BillingAccountChip";
import {
  BillingAccountSubscription,
  getMovableSimIds,
} from "@/components/Portal/Documents/BillingAccounts/billingAccountUtils";
import { useIsMobile } from "@/hooks/useIsMobile";

interface SelectionFormValues {
  simIds: string[];
}

interface Props {
  subscriptions: BillingAccountSubscription[];
  count: number;
  /** Unset hides the move buttons: no other Vertragskonto to move an Abo to. */
  onMove?: (subscription: BillingAccountSubscription) => void;
  /** Unset hides the Sammelverschiebung: no other Vertragskonto to move to. */
  onBulkMove?: (simIds: number[]) => void;
  /** Sammelverschiebung: the Abos are picked via checkboxes. */
  isSelecting?: boolean;
  onSelectingChange?: (isSelecting: boolean) => void;
}

const textClassName =
  "min-w-0 flex-1 truncate px-3 text-base font-semibold leading-md text-foreground-primary";

/**
 * "Zugeordnete Abos" of a Vertragskonto. The first click on Sammelverschiebung
 * swaps the move buttons for checkboxes, the second one hands the picked Abos
 * on to the Sammelverschiebung page.
 */
export const AssignedSubscriptions: FC<Props> = ({
  subscriptions,
  count,
  onMove,
  onBulkMove,
  isSelecting = false,
  onSelectingChange,
}) => {
  const { t } = useTranslation("portal", {
    keyPrefix: "user-account.billingAccounts.subscriptions",
  });
  const isMobile = useIsMobile();
  const {
    register,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<SelectionFormValues>({ defaultValues: { simIds: [] } });
  const selectedSimIds = watch("simIds");
  const movableSimIds = getMovableSimIds(subscriptions);
  const hasMovableSubscription = movableSimIds.length > 0;
  const bulkMoveLabel = isSelecting
    ? t("moveSelected", { count: selectedSimIds.length })
    : t("bulkMove");
  const isBulkMoveIconOnly = isMobile && !isSelecting;

  const handleBulkMove = () => {
    if (!isSelecting) {
      onSelectingChange?.(true);

      return;
    }

    onBulkMove?.(selectedSimIds.map(Number));
  };

  const cancelSelecting = () => {
    reset();
    onSelectingChange?.(false);
  };

  return (
    <div data-testid="billing-account-subscriptions" className="flex flex-col">
      <div
        className={clsx(
          "flex items-start gap-3 bg-surface-quaternary pb-2 pl-4 pt-4",
          // While selecting, the select-all takes the rows' trailing slot, so
          // the header's right padding matches the rows' `p-1`.
          isSelecting ? "pr-1" : "pr-2",
        )}
      >
        <div className="flex min-w-0 flex-1 items-center gap-2 py-2">
          <h3 className="truncate text-base font-semibold leading-md tracking-wide text-foreground-primary">
            {t("headline")}
          </h3>
          <BillingAccountChip text={count} tone="info" />
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {isSelecting && (
            <Button
              dataTestid="billing-account-bulk-cancel"
              variant="secondary"
              size="sm"
              onClick={cancelSelecting}
            >
              {t("cancel")}
            </Button>
          )}
          {onBulkMove && (
            <Button
              dataTestid="billing-account-bulk-move"
              variant="primary"
              size="sm"
              icon={<Move />}
              ariaLabel={isBulkMoveIconOnly ? bulkMoveLabel : undefined}
              disabled={
                isSelecting
                  ? selectedSimIds.length === 0
                  : !hasMovableSubscription
              }
              onClick={handleBulkMove}
            >
              {isBulkMoveIconOnly ? undefined : bulkMoveLabel}
            </Button>
          )}
          {isSelecting && (
            <div className="flex w-10 shrink-0 justify-center">
              <TableSelectAllCheckbox
                dataTestid="billing-account-select-all"
                currentPageIds={movableSimIds}
                selectedIds={selectedSimIds}
                labels={{
                  selectAll: t("selectAll"),
                  deselectAll: t("deselectAll"),
                }}
                onChange={(simIds) => setValue("simIds", simIds)}
              />
            </div>
          )}
        </div>
      </div>
      {subscriptions.length === 0 ? (
        <p className="px-4 py-5 text-sm text-foreground-secondary">
          {t("empty")}
        </p>
      ) : (
        <ul className="flex flex-col divide-y divide-border-tertiary">
          {subscriptions.map((subscription, index) => {
            const checkboxId = `billing-account-select-${index}`;

            return (
              <li
                key={`${subscription.label}-${index}`}
                className="flex h-16 items-center gap-2 p-1"
              >
                {isSelecting ? (
                  <label
                    className={clsx(textClassName, "cursor-pointer")}
                    htmlFor={checkboxId}
                  >
                    {subscription.label}
                  </label>
                ) : (
                  <p className={textClassName}>{subscription.label}</p>
                )}
                {isSelecting ? (
                  <div className="flex w-10 shrink-0 justify-center">
                    <Checkbox<SelectionFormValues>
                      id={checkboxId}
                      name="simIds"
                      value={subscription.simId}
                      disabled={subscription.simId == null}
                      options={{}}
                      errors={errors}
                      register={register}
                    />
                  </div>
                ) : (
                  onMove && (
                    <div className="flex w-10 shrink-0 justify-center">
                      <Button
                        dataTestid="billing-account-move"
                        variant="secondary"
                        size="sm"
                        icon={<Move />}
                        ariaLabel={t("move")}
                        disabled={subscription.simId == null}
                        onClick={() => onMove(subscription)}
                      />
                    </div>
                  )
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
